import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MyAccount } from '../../../../../../models/my-account';
import { AccountService } from '../../../../../../services/account/account.service';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { EntryPass, EntryPassGiftingConfig } from '../../../../../../models/entry-pass';
import { EntryPassStatus } from '../../../../../../models/entry-pass-enums';
import { EntryPassService } from '../../../../../../services/entry-pass/entry-pass.service';


@Component({
  selector: 'app-entry-pass-gifting-config-detail',
  templateUrl: './entry-pass-gifting-config-detail.component.html',
  styleUrls: ['./entry-pass-gifting-config-detail.component.css']
})
export class EntryPassGiftingConfigDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewConfig: boolean;
  public giftingConfig: EntryPassGiftingConfig = {
    id: null,
    url: null,
    assigned_entry_pass_ids: [],
    assigned_entry_pass_names: [],
    max_future_delivery_date_days: 366,
    expiration_days: 366,
    gift_giver_terms_and_conditions: '',
    gift_recipient_terms_and_conditions: '',
    created_at: null,
    updated_at: null
  };
  public entryPasses: EntryPass[];

  public unsavedChanges = false;
  public errorMessage = '';
  public errors: any = {};

  public loadingExistingConfig = false;
  public loadingEntryPasses = true;
  public loadingSubmit = false;
  public loadingDelete = false;

  public configNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  public EntryPassStatus = EntryPassStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private entryPassService: EntryPassService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewConfig = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    if (!this.isNewConfig) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.configNotFound = true;
        this.loadingExistingConfig = false;
        return;
      }

      this.loadGiftingConfig(idInt);
    }

    this.loadEntryPasses();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public getFilteredEntryPasses(): EntryPass[] {
    return this.entryPasses.filter(m => {
      return (
        [
          EntryPassStatus.draft,
          EntryPassStatus.active
        ].includes(m.status)
      );
    })
  }

  public entryPassSelected(entryPass: EntryPass) {
    return this.giftingConfig.assigned_entry_pass_ids.includes(entryPass.id);
  }

  public toggleEntryPass(entryPass: EntryPass) {
    if (this.entryPassSelected(entryPass)) {
      this.giftingConfig.assigned_entry_pass_ids.splice(
        this.giftingConfig.assigned_entry_pass_ids.indexOf(entryPass.id),
        1
      );
    } else {
      this.giftingConfig.assigned_entry_pass_ids.push(entryPass.id);
    }
    this.unsavedChanges = true;
  }

  public allEntryPassesAreSelected() {
    for (const entryPass of this.getFilteredEntryPasses()) {
      if (!this.giftingConfig.assigned_entry_pass_ids.includes(entryPass.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoEntryPassesSelected() {
    this.giftingConfig.assigned_entry_pass_ids = [];
    this.unsavedChanges = true;
  }

  public setAllEntryPassesSelected() {
    this.giftingConfig.assigned_entry_pass_ids = this.getFilteredEntryPasses().map(m => m.id);
    this.unsavedChanges = true;
  }

  public isLoading() {
    return (
      this.loadingExistingConfig ||
      this.loadingEntryPasses ||
      this.loadingSubmit ||
      this.loadingDelete
    )
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<EntryPassGiftingConfig>;
    if (this.isNewConfig) {
      savePromise = this.entryPassService.addEntryPassGiftingConfig(this.giftingConfig);
    } else {
      savePromise = this.entryPassService.updateEntryPassGiftingConfig(this.giftingConfig);
    }

    savePromise
      .then(savedConfig => {
        return this.entryPassService
          .assignGiftingConfigToEntryPasses(
            savedConfig.id,
            this.giftingConfig.assigned_entry_pass_ids
          )
          .then(() => {
            this.unsavedChanges = false;

            let successMessage: string;
            if (this.isNewConfig) {
              successMessage = 'Entry pass gifting config created successfully.';
            } else {
              successMessage = 'Entry pass gifting config updated successfully.';
            }

            this.router.navigate(
              ['/gifting-config'],
              {
                state: {
                  feedback: {
                    text: successMessage,
                    status: 'success'
                  }
                }
              }
            );
          })
          .catch(reason => {
            if (reason.status === 400) {
              this.errorMessage = reason.error;
            } else if (reason.status === 403) {
              if (this.isNewConfig) {
                this.errorMessage = 'You do not have permission to add entry pass gifting configurations.';
              } else {
                this.errorMessage = 'You do not have permission to modify this entry pass gifting configuration.';
              }
            } else {
              this.serverError = reason.status;
            }
          });
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewConfig) {
            this.errorMessage = 'You do not have permission to add entry pass gifting configurations.';
          } else {
            this.errorMessage = 'You do not have permission to modify this entry pass gifting configuration.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Entry Pass Gifting Configuration';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete ' +
      'this entry pass gifting config? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.entryPassService
        .deleteEntryPassGiftingConfig(this.giftingConfig)
        .then(() => {
          this.unsavedChanges = false;

          this.router.navigate(
            ['/gifting-config'],
            {
              state: {
                feedback: {
                  text: 'Entry pass gifting configuration deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this entry pass gifting configuration.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  private loadEntryPasses() {
    this.loadingEntryPasses = true;

    this.entryPassService
      .getEntryPasses(1, 3000, {})
      .then(result => {
        this.entryPasses = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.configNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEntryPasses = false; });
  }

  private loadGiftingConfig(id: number) {
    this.loadingExistingConfig = true;

    this.entryPassService
      .getEntryPassGiftingConfig(id)
      .then(config => {
        this.giftingConfig = config;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.configNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingConfig = false; });
  }
}
